import React from "react";
import { usePermission } from "@/features/auth/hooks/usePermission";
import { useVerifiedPermissions } from "@/features/auth/hooks/useVerifiedPermissions";
import { useHelpCenterStore } from "@/features/help-center/stores/helpCenterStore";
import { navigate } from "utilities/navigate";
import useCanPlayPickems from "@/hooks/useCanPlayPickems";
import type { Alert } from "@/features/betslip/types";
import { SlipAlert } from "@/features/betslip/components/parts/SlipAlert";

import * as styles from "@/features/betslip/components/GlobalAlerts.module.scss";

export const useAccountAlert = (): Alert | null => {
  const {
    permissions: { submitBet },
    isSettling,
  } = useVerifiedPermissions("submitBet");
  const viewCasino = usePermission("viewCasino");
  const playCasino = usePermission("playPaidCasino");
  const pickemsAvailable = useCanPlayPickems();

  if (submitBet === "BLOCKED") {
    return {
      variant: "danger",
      icon: "location",
      message: "Your account is under review",
      cta: { label: "Contact Support", action: "openChat" },
    };
  }

  if (submitBet === "COOLDOWN") {
    return {
      variant: "danger",
      icon: "clock",
      message: "Your account is in Cooldown",
      cta: {
        label: "Learn More",
        action: "navigate",
        payload: "/preferences/account-deactivations",
      },
    };
  }

  if (submitBet === "ACCOUNT_REOPEN_REQUIRED") {
    return {
      variant: "danger",
      icon: "lock",
      message: "Your account requires reopening",
      cta: { label: "Reopen", action: "navigate", payload: "/account/reopen" },
    };
  }

  if (submitBet === "VERIFICATION_STATUS_UNSUPPORTED") {
    // permissions may still be catching up
    if (isSettling) return null;

    return {
      variant: "danger",
      icon: "lock",
      message: "Your account requires verification",
      cta: {
        label: "Verify",
        action: "navigate",
        payload: "/account/verification",
      },
    };
  }

  if (
    submitBet === "PHYSICAL_LOCATION_UNSUPPORTED" ||
    submitBet === "RESIDENTIAL_LOCATION_UNSUPPORTED"
  ) {
    const casinoAvailable =
      viewCasino === "GRANTED" && playCasino === "GRANTED";
    const cta = casinoAvailable
      ? { label: "Try Casino", action: "navigate", payload: "/casino/lobby" }
      : pickemsAvailable
        ? { label: "Try Pick'ems", action: "navigate", payload: "/pickems" }
        : undefined;

    return {
      variant: "danger",
      icon: "location",
      message: "Betting is not available in your location",
      cta,
    };
  }

  return null;
};

export const UnauthorizedAlerts = () => {
  const alert = useAccountAlert();
  const openHelpCenterChat = useHelpCenterStore((s) => s.openHelpCenterChat);

  if (!alert) return null;

  return (
    <div data-testid="account-alerts" className={styles.container}>
      <SlipAlert
        alert={alert}
        onCta={(cta) => {
          if (cta.action === "openChat") {
            openHelpCenterChat();
          } else if (typeof cta.payload === "string") {
            navigate(cta.payload);
          }
        }}
      />
    </div>
  );
};
